/* Added the following code for progress_tracking.ray using AI Agent. 21/07/2026. Code Start */
/**
 * Client helpers for learning progress APIs (/api/user/progress/*).
 */
import { BASE_URL } from "./config";

function authHeaders() {
  const token = localStorage.getItem("token");
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
  /* Added the following code for FAST.Ray using AI Agent. 24/07/2026. Code Start */
  // Force lock evaluation even for Super Admin while previewing as learner.
  if (isLearnerPreviewActive()) {
    headers["X-Learner-Preview"] = "1";
  }
  /* Code end. Ray using AI Agent. 24/07/2026 */
  return headers;
}

async function parseJson(response) {
  const status = response.status;
  let body = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  return { status, body };
}

export function normalizePageSlug(pathOrSlug) {
  if (!pathOrSlug || typeof pathOrSlug !== "string") return "";
  return pathOrSlug.trim().replace(/^\/+/, "").toLowerCase();
}

export async function fetchMyProgress(pageSlug, options = {}) {
  const slug = normalizePageSlug(pageSlug);
  const params = new URLSearchParams();
  if (slug) params.set("pageSlug", slug);
  /* Added the following code for FAST.Ray using AI Agent. 24/07/2026. Code Start */
  // lite=1: sidebar rail on /home — skips lockMap / heavy unlock work.
  if (options.lite === true || options.lite === 1 || options.lite === "1") {
    params.set("lite", "1");
  }
  /* Code end. Ray using AI Agent. 24/07/2026 */
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await fetch(`${BASE_URL}/api/user/progress/me${qs}`, {
    method: "GET",
    headers: authHeaders(),
  });
  return parseJson(res);
}

export async function recordProgressVisit(pageSlug, extra = {}) {
  /* Added the following code for FAST.Ray using AI Agent. 23/07/2026. Code Start */
  invalidateLearningPathCache();
  /* Code end. Ray using AI Agent. 23/07/2026 */
  const res = await fetch(`${BASE_URL}/api/user/progress/visit`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({
      pageSlug: normalizePageSlug(pageSlug),
      ...extra,
    }),
  });
  /* Added the following code for NOJIRA.Ray using AI Agent. July 27, 2026. Code Start */
  // Drop any hover-prefetch that finished with pre-write data while this visit ran.
  const parsed = await parseJson(res);
  if (parsed.status >= 200 && parsed.status < 300) {
    invalidateLearningPathCache();
  }
  return parsed;
  /* Code end. Ray using AI Agent. July 27, 2026 */
  /* Commenting the following code for NOJIRA.Ray using AI Agent. July 27, 2026. Comment Start */
  // return parseJson(res);
  /* Commenting end. Ray using AI Agent. July 27, 2026 */
}

export async function recordProgressEvent(payload) {
  const body = { ...payload };
  if (body.pageSlug) body.pageSlug = normalizePageSlug(body.pageSlug);
  if (body.pagePath) body.pagePath = normalizePageSlug(body.pagePath);
  /* Added the following code for FAST.Ray using AI Agent. 23/07/2026. Code Start */
  invalidateLearningPathCache();
  /* Code end. Ray using AI Agent. 23/07/2026 */
  const res = await fetch(`${BASE_URL}/api/user/progress/events`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  /* Added the following code for NOJIRA.Ray using AI Agent. July 27, 2026. Code Start */
  // Same post-write invalidate so stale path/hub prefetch cannot repopulate the cache.
  const parsed = await parseJson(res);
  if (parsed.status >= 200 && parsed.status < 300) {
    invalidateLearningPathCache();
  }
  return parsed;
  /* Code end. Ray using AI Agent. July 27, 2026 */
  /* Commenting the following code for NOJIRA.Ray using AI Agent. July 27, 2026. Comment Start */
  // return parseJson(res);
  /* Commenting end. Ray using AI Agent. July 27, 2026 */
}

/* Added the following code for FAST.Ray using AI Agent. 23/07/2026. Code Start */
/** Short-lived in-memory cache so My Learning ↔ Learning Path / hover prefetch reuse work. */
const learningPathCache = new Map();
const LEARNING_PATH_CACHE_MS = 30000;
/* Added the following code for NOJIRA.Ray using AI Agent. July 27, 2026. Code Start */
/**
 * Bumped on every invalidate so in-flight prefetch cannot write stale results
 * back into the map after clear (hover-prefetch vs progress-write race).
 */
let learningPathCacheEpoch = 0;
/* Code end. Ray using AI Agent. July 27, 2026 */

export function invalidateLearningPathCache() {
  /* Added the following code for NOJIRA.Ray using AI Agent. July 27, 2026. Code Start */
  learningPathCacheEpoch += 1;
  /* Code end. Ray using AI Agent. July 27, 2026 */
  learningPathCache.clear();
}

export async function fetchLearningPath(options = {}) {
  /* Added the following code for NOJIRA.Ray using AI Agent. July 27, 2026. Code Start */
  // Callers that must not reuse hover-prefetch (e.g. My Learning entry) pass refresh.
  if (options.refresh === true || options.force === true) {
    invalidateLearningPathCache();
  }
  /* Code end. Ray using AI Agent. July 27, 2026 */
  const params = new URLSearchParams();
  if (
    options.summary === true ||
    options.summary === 1 ||
    options.summary === "1"
  ) {
    params.set("summary", "1");
  }
  if (options.hub === true || options.hub === 1 || options.hub === "1") {
    params.set("hub", "1");
  }
  const qs = params.toString() ? `?${params.toString()}` : "";
  const cacheKey = qs || "full";
  const now = Date.now();
  const hit = learningPathCache.get(cacheKey);
  if (hit?.result && now - hit.at < LEARNING_PATH_CACHE_MS) {
    return hit.result;
  }
  if (hit?.inflight) {
    return hit.inflight;
  }

  /* Added the following code for NOJIRA.Ray using AI Agent. July 27, 2026. Code Start */
  const epochAtStart = learningPathCacheEpoch;
  const inflight = (async () => {
    const res = await fetch(`${BASE_URL}/api/user/progress/path${qs}`, {
      method: "GET",
      headers: authHeaders(),
    });
    const parsed = await parseJson(res);
    // Skip cache write if invalidate ran while this request was in flight.
    if (epochAtStart === learningPathCacheEpoch) {
      learningPathCache.set(cacheKey, { at: Date.now(), result: parsed });
    }
    return parsed;
  })();

  if (epochAtStart === learningPathCacheEpoch) {
    learningPathCache.set(cacheKey, { at: now, inflight });
  }
  try {
    return await inflight;
  } catch (err) {
    if (epochAtStart === learningPathCacheEpoch) {
      learningPathCache.delete(cacheKey);
    }
    throw err;
  }
  /* Code end. Ray using AI Agent. July 27, 2026 */
  /* Commenting the following code for NOJIRA.Ray using AI Agent. July 27, 2026. Comment Start */
  // const inflight = (async () => {
  //   const res = await fetch(`${BASE_URL}/api/user/progress/path${qs}`, {
  //     method: "GET",
  //     headers: authHeaders(),
  //   });
  //   const parsed = await parseJson(res);
  //   learningPathCache.set(cacheKey, { at: Date.now(), result: parsed });
  //   return parsed;
  // })();
  //
  // learningPathCache.set(cacheKey, { at: now, inflight });
  // try {
  //   return await inflight;
  // } catch (err) {
  //   learningPathCache.delete(cacheKey);
  //   throw err;
  // }
  /* Commenting end. Ray using AI Agent. July 27, 2026 */
}
/* Code end. Ray using AI Agent. 23/07/2026 */

export async function fetchProgressLocks() {
  const res = await fetch(`${BASE_URL}/api/user/progress/locks`, {
    method: "GET",
    headers: authHeaders(),
  });
  return parseJson(res);
}

export async function checkProgressAccess(pageSlug) {
  const slug = normalizePageSlug(pageSlug);
  const qs = `?pageSlug=${encodeURIComponent(slug)}`;
  const res = await fetch(`${BASE_URL}/api/user/progress/access${qs}`, {
    method: "GET",
    headers: authHeaders(),
  });
  return parseJson(res);
}

/* Added the following code for FAST.Ray using AI Agent. 27/07/2026. Code Start */
export async function fetchCohortProgress(chapterRef = "ch1", filters = {}) {
/* Code end. Ray using AI Agent. 27/07/2026 */
/* Commenting the following code for FAST.Ray using AI Agent. 27/07/2026. Comment Start */
// export async function fetchCohortProgress(chapterRef = "ch4", filters = {}) {
/* Commenting end. Ray using AI Agent. 27/07/2026 */
  const params = new URLSearchParams();
  params.set("chapterRef", chapterRef);
  if (filters.status) params.set("status", filters.status);
  if (filters.inactiveDays) params.set("inactiveDays", String(filters.inactiveDays));
  if (filters.stuckOn) params.set("stuckOn", filters.stuckOn);
  if (filters.role) params.set("role", filters.role);
  if (filters.cohortTag) params.set("cohortTag", filters.cohortTag);
  if (filters.learner) params.set("learner", filters.learner);
  const res = await fetch(
    `${BASE_URL}/api/user/progress/admin/cohort?${params.toString()}`,
    {
      method: "GET",
      headers: authHeaders(),
    }
  );
  return parseJson(res);
}

/** Download cohort CSV (returns blob URL helper result). */
/* Added the following code for FAST.Ray using AI Agent. 27/07/2026. Code Start */
export async function downloadCohortCsv(chapterRef = "ch1", filters = {}) {
/* Code end. Ray using AI Agent. 27/07/2026 */
/* Commenting the following code for FAST.Ray using AI Agent. 27/07/2026. Comment Start */
// export async function downloadCohortCsv(chapterRef = "ch4", filters = {}) {
/* Commenting end. Ray using AI Agent. 27/07/2026 */
  const params = new URLSearchParams();
  params.set("chapterRef", chapterRef);
  params.set("format", "csv");
  if (filters.status) params.set("status", filters.status);
  if (filters.inactiveDays) params.set("inactiveDays", String(filters.inactiveDays));
  if (filters.stuckOn) params.set("stuckOn", filters.stuckOn);
  if (filters.role) params.set("role", filters.role);
  if (filters.cohortTag) params.set("cohortTag", filters.cohortTag);
  if (filters.learner) params.set("learner", filters.learner);
  const res = await fetch(
    `${BASE_URL}/api/user/progress/admin/cohort?${params.toString()}`,
    {
      method: "GET",
      headers: authHeaders(),
    }
  );
  if (!res.ok) {
    const parsed = await parseJson(res);
    return { status: res.status, body: parsed.body, blob: null };
  }
  const blob = await res.blob();
  return { status: res.status, body: null, blob };
}

export async function fetchUnlockRules(chapterRef) {
  const qs = chapterRef ? `?chapterRef=${encodeURIComponent(chapterRef)}` : "";
  const res = await fetch(`${BASE_URL}/api/user/progress/admin/rules${qs}`, {
    method: "GET",
    headers: authHeaders(),
  });
  return parseJson(res);
}

export async function saveUnlockRule(rule) {
  const res = await fetch(`${BASE_URL}/api/user/progress/admin/rules`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(rule),
  });
  return parseJson(res);
}

export async function deleteUnlockRule(id) {
  const res = await fetch(`${BASE_URL}/api/user/progress/admin/rules/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return parseJson(res);
}

export async function overrideUnlockForUser(payload) {
  const res = await fetch(`${BASE_URL}/api/user/progress/admin/override`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return parseJson(res);
}

/* Added the following code for progress_tracking.ray using AI Agent. 21/07/2026. Code Start */
export async function bulkOverrideUnlock(payload) {
  const res = await fetch(`${BASE_URL}/api/user/progress/admin/bulk-override`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return parseJson(res);
}

export async function bulkResetProgress(payload) {
  const res = await fetch(`${BASE_URL}/api/user/progress/admin/bulk-reset`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return parseJson(res);
}

export async function fetchPageProgressConfig(pageSlug) {
  const qs = `?pageSlug=${encodeURIComponent(normalizePageSlug(pageSlug))}`;
  const res = await fetch(`${BASE_URL}/api/user/progress/admin/page-config${qs}`, {
    method: "GET",
    headers: authHeaders(),
  });
  return parseJson(res);
}

export async function savePageProgressConfig(config) {
  const res = await fetch(`${BASE_URL}/api/user/progress/admin/page-config`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({
      ...config,
      pageSlug: normalizePageSlug(config.pageSlug),
    }),
  });
  return parseJson(res);
}

export async function fetchProgressAudit(limit = 50) {
  const qs = `?limit=${encodeURIComponent(limit)}`;
  const res = await fetch(`${BASE_URL}/api/user/progress/admin/audit${qs}`, {
    method: "GET",
    headers: authHeaders(),
  });
  return parseJson(res);
}

/** Format missing checklist for lock toasts / tooltips. */
export function formatMissingChecklist(items) {
  if (!Array.isArray(items) || items.length === 0) return "";
  return items.map((i) => i.label || i.id).filter(Boolean).join(", ");
}

/* Added the following code for smarterresume.ray using AI Agent. 22/07/2026. Code Start */
/** Build react-router state so Resume opens the next incomplete accordion. */
export function buildResumeNavState({ pageName, resume, lockMap, access } = {}) {
  const resumeState =
    resume?.resumeState ||
    access?.resumeState ||
    lockMap?.resumeState ||
    null;
  return {
    pageName: pageName || resume?.label || lockMap?.resumeLabel || undefined,
    resumeState,
  };
}
/* Code end. ray using AI Agent. 22/07/2026 */

/* Added the following code for progress_tracking.ray using AI Agent. 22/07/2026. Code Start */
export async function fetchLearnerDetail(userId, chapterRef) {
  const params = new URLSearchParams();
  if (chapterRef) params.set("chapterRef", chapterRef);
  const qs = params.toString() ? `?${params}` : "";
  const res = await fetch(
    `${BASE_URL}/api/user/progress/admin/learner/${encodeURIComponent(userId)}${qs}`,
    { method: "GET", headers: authHeaders() }
  );
  return parseJson(res);
}

/* Commenting the following code for progress.ray using AI Agent. 30/07/2026. Comment Start */
// export async function unlockNextForLearner(payload) {
//   const res = await fetch(`${BASE_URL}/api/user/progress/admin/unlock-next`, {
//     method: "POST",
//     headers: authHeaders(),
//     body: JSON.stringify(payload),
//   });
//   return parseJson(res);
// }
/* Commenting end. ray using AI Agent. 30/07/2026 */

export async function approveChapterForLearner(payload) {
  const res = await fetch(`${BASE_URL}/api/user/progress/admin/approve-chapter`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return parseJson(res);
}

export async function fetchViewAsProgress(userId, pageSlug) {
  const params = new URLSearchParams();
  if (pageSlug) params.set("pageSlug", normalizePageSlug(pageSlug));
  const qs = params.toString() ? `?${params}` : "";
  const res = await fetch(
    `${BASE_URL}/api/user/progress/admin/view-as/${encodeURIComponent(userId)}${qs}`,
    { method: "GET", headers: authHeaders() }
  );
  return parseJson(res);
}

export async function sendAtRiskDigest(payload) {
  const res = await fetch(`${BASE_URL}/api/user/progress/admin/at-risk-digest`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return parseJson(res);
}

export async function setLearnerCohortTag(payload) {
  const res = await fetch(`${BASE_URL}/api/user/progress/admin/cohort-tag`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return parseJson(res);
}

export async function fetchCohortTags() {
  const res = await fetch(`${BASE_URL}/api/user/progress/admin/cohort-tags`, {
    method: "GET",
    headers: authHeaders(),
  });
  return parseJson(res);
}

export async function downloadProgressAuditCsv(limit = 500) {
  const qs = `?format=csv&limit=${encodeURIComponent(limit)}`;
  const res = await fetch(`${BASE_URL}/api/user/progress/admin/audit${qs}`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const parsed = await parseJson(res);
    return { status: res.status, body: parsed.body, blob: null };
  }
  const blob = await res.blob();
  return { status: res.status, body: null, blob };
}

const VIEW_AS_KEY = "selftrainai_progress_view_as";
/* Added the following code for FAST.Ray using AI Agent. 24/07/2026. Code Start */
const LEARNER_PREVIEW_KEY = "selftrainai_learner_preview";
/* Code end. Ray using AI Agent. 24/07/2026 */

export function getViewAsSession() {
  try {
    const raw = sessionStorage.getItem(VIEW_AS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setViewAsSession(payload) {
  if (!payload) {
    sessionStorage.removeItem(VIEW_AS_KEY);
    /* Added the following code for progress_tracking.ray using AI Agent. 24/07/2026. Code Start */
    // Leaving impersonation — restore admin chrome (same exit path as Preview as learner).
    try {
      if (sessionStorage.getItem("selftrainai_admin_restore") === "1") {
        localStorage.setItem("isAdmin", "true");
        sessionStorage.removeItem("selftrainai_admin_restore");
      }
    } catch {
      /* ignore */
    }
    window.dispatchEvent(new CustomEvent("selftrainai:view-as-changed"));
    window.dispatchEvent(new CustomEvent("selftrainai:learner-preview-changed"));
    window.dispatchEvent(new Event("auth-login"));
    /* Code end. ray using AI Agent. 24/07/2026 */
    return;
  }
  /* Added the following code for progress_tracking.ray using AI Agent. 24/07/2026. Code Start */
  // View as student: learner-shaped UI (hide admin chrome), like Preview as learner,
  // but progress/locks come from the selected student (read-only).
  try {
    sessionStorage.removeItem(LEARNER_PREVIEW_KEY);
    if (localStorage.getItem("isAdmin") === "true") {
      sessionStorage.setItem("selftrainai_admin_restore", "1");
      localStorage.setItem("isAdmin", "false");
    }
  } catch {
    /* ignore */
  }
  sessionStorage.setItem(VIEW_AS_KEY, JSON.stringify(payload));
  window.dispatchEvent(new CustomEvent("selftrainai:learner-preview-changed"));
  window.dispatchEvent(new CustomEvent("selftrainai:view-as-changed"));
  window.dispatchEvent(new Event("auth-login"));
  /* Code end. ray using AI Agent. 24/07/2026 */
  /* Commenting the following code for progress_tracking.ray using AI Agent. 24/07/2026. Comment Start */
  // // Impersonation wins over self-preview — restore admin chrome if needed.
  // try {
  //   sessionStorage.removeItem(LEARNER_PREVIEW_KEY);
  //   if (sessionStorage.getItem("selftrainai_admin_restore") === "1") {
  //     localStorage.setItem("isAdmin", "true");
  //     sessionStorage.removeItem("selftrainai_admin_restore");
  //   }
  // } catch {
  //   /* ignore */
  // }
  // window.dispatchEvent(new CustomEvent("selftrainai:learner-preview-changed"));
  // sessionStorage.setItem(VIEW_AS_KEY, JSON.stringify(payload));
  // window.dispatchEvent(new CustomEvent("selftrainai:view-as-changed"));
  /* Commenting end. ray using AI Agent. 24/07/2026 */
}

export function clearViewAsSession() {
  setViewAsSession(null);
}

/* Added the following code for FAST.Ray using AI Agent. 24/07/2026. Code Start */
/** Admin "Preview as myself" — learner UI + locks on own account (not view-as). */
export function isLearnerPreviewActive() {
  try {
    return sessionStorage.getItem(LEARNER_PREVIEW_KEY) === "1";
  } catch {
    return false;
  }
}

export function setLearnerPreviewActive(on) {
  try {
    if (on) {
      sessionStorage.setItem(LEARNER_PREVIEW_KEY, "1");
      // Preview is self; clear impersonation so the two modes never stack.
      sessionStorage.removeItem(VIEW_AS_KEY);
      window.dispatchEvent(new CustomEvent("selftrainai:view-as-changed"));
      /* Added the following code for FAST.Ray using AI Agent. 24/07/2026. Code Start */
      // Hide admin editor chrome site-wide; restore on exit.
      if (localStorage.getItem("isAdmin") === "true") {
        sessionStorage.setItem("selftrainai_admin_restore", "1");
        localStorage.setItem("isAdmin", "false");
      }
      /* Code end. Ray using AI Agent. 24/07/2026 */
    } else {
      sessionStorage.removeItem(LEARNER_PREVIEW_KEY);
      /* Added the following code for FAST.Ray using AI Agent. 24/07/2026. Code Start */
      if (sessionStorage.getItem("selftrainai_admin_restore") === "1") {
        localStorage.setItem("isAdmin", "true");
        sessionStorage.removeItem("selftrainai_admin_restore");
      }
      /* Code end. Ray using AI Agent. 24/07/2026 */
    }
  } catch {
    /* ignore storage errors */
  }
  window.dispatchEvent(new CustomEvent("selftrainai:learner-preview-changed"));
  /* Added the following code for FAST.Ray using AI Agent. 24/07/2026. Code Start */
  // Remount lesson editors / nav that only read isAdmin on mount.
  window.dispatchEvent(new Event("auth-login"));
  /* Code end. Ray using AI Agent. 24/07/2026 */
}

export function clearLearnerPreview() {
  setLearnerPreviewActive(false);
}

/** True if the signed-in user is an admin (even while Preview as myself hides chrome). */
export function isRealAdminUser() {
  try {
    return (
      localStorage.getItem("isAdmin") === "true" ||
      sessionStorage.getItem("selftrainai_admin_restore") === "1"
    );
  } catch {
    return localStorage.getItem("isAdmin") === "true";
  }
}

/* Added the following code for FAST.Ray using AI Agent. 24/07/2026. Code Start */
// Survive refresh: keep admin chrome suppressed while preview session is active.
try {
  if (
    typeof window !== "undefined" &&
    sessionStorage.getItem(LEARNER_PREVIEW_KEY) === "1" &&
    localStorage.getItem("isAdmin") === "true"
  ) {
    sessionStorage.setItem("selftrainai_admin_restore", "1");
    localStorage.setItem("isAdmin", "false");
  }
  /* Added the following code for progress_tracking.ray using AI Agent. 24/07/2026. Code Start */
  // Same for View as student (impersonation session).
  if (
    typeof window !== "undefined" &&
    sessionStorage.getItem(VIEW_AS_KEY) &&
    localStorage.getItem("isAdmin") === "true"
  ) {
    sessionStorage.setItem("selftrainai_admin_restore", "1");
    localStorage.setItem("isAdmin", "false");
  }
  /* Code end. ray using AI Agent. 24/07/2026 */
} catch {
  /* ignore */
}
/* Code end. Ray using AI Agent. 24/07/2026 */
/* Code end. Ray using AI Agent. 24/07/2026 */
/* Code end. ray using AI Agent. 22/07/2026 */
/* Code end. ray using AI Agent. 21/07/2026 */
